// Libs
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  NetworkStatus,
  useLazyQuery,
  useMutation,
  useQuery
} from '@apollo/client';

// Components and icons
import SearchableDropDown from './SearchableDropDown';
import {
  Button,
  UserChip,
  Flexbox,
  SearchBox,
  TextInput,
  UserSearchBox,
  SingleSelect
} from '@ibinder/common-frontend';
import { search } from '@ibinder/common-icons';

// Queries
import {
  GET_ALL_USERS,
  GET_ALL_MARKETS,
  POST_ORGANIZATION
} from '../../../../../../src/api/queries/queries_local';
import { encodeUserSearchQuery } from '@ibinder/common-utils';

import { GET_ALL_PACKAGES } from 'Shared/api/queries/getOrganizationPackages.query';
import { sortPackages } from './OrganizationViewer/OrganizationPackages';
import { useFeatureToggle, useIBTranslation } from '@ibinder/ui-common-hooks';

const Container = styled(Flexbox)`
  animation: identifier 350ms;
  height: 100%;
  overflow-y: auto;
  flex-direction: column;
  padding: 1rem;

  @keyframes identifier {
    0% {
      opacity: 0;
      transform: translateY(10px);
    }
    100% {
      opacity: 1;
    }
  }

  & > .input-field {
    margin: 1rem 0;
  }
`;
const ResponseMessage = styled.h2`
  color: ${(p) =>
    p.hasErrors ? p.theme.colors.error : p.theme.colors.success};
`;
const BottomActionBar = styled(Flexbox)`
  margin-top: auto;
  box-shadow: ${(p) => p.theme.shadows.primaryWithOpacity};
  padding: 1rem;
`;
const StyledSubHeading = styled.h3`
  font-size: ${(p) => (p.small ? '1rem' : '1.125rem')};
  color: ${(p) =>
    p.small ? p.theme.colors.discrete70 : p.theme.colors.discrete};
`;

const Spacing = styled.div<{ padding?: string }>`
  padding: ${(p) => p.padding || '1rem'};
`;

interface phoneNumbers {
  phoneType: string;
  number: string;
}
interface User {
  id: string;
  email: string;
  fullName: string;
  firstName: string;
  lastName: string;
  lastUpdated: string;
  company: {
    city: string;
    name: string;
  };
  phone: string;
}
enum Platforms {
  Azure = 'Azure',
  Exoscale = 'Exoscale'
}
interface Organization {
  name: string;
  marketId: string;
  productFeatures: any[];
  packages: any[];
  organizationAdministrator: User;
  platform?: Platforms;
}

export const DEFAULT_PLATFORM = Platforms.Azure;

export const platformData = [
  { Key: null, Value: 'Select platform...' },
  { Key: Platforms.Azure, Value: 'International (default)' },
  { Key: Platforms.Exoscale, Value: 'EU (compliant)' }
];

const CreateOrgForm = ({ onClose, setIsCreateOrg, handleSetOrganization }) => {
  const [userData, setUserData] = useState(undefined);
  const [orgInputValue, setOrgInputValue] = useState('');
  const [packageValue, setPackageValue] = useState(0);
  const { t } = useIBTranslation();
  const [packages, setPackages] = useState([
    {
      Key: null,
      Value: 'Select package...',
      package: null
    }
  ]);
  const [selectedValues, setSelectedValues] = useState({
    market: null,
    orgName: null,
    user: null,
    package: null
  });

  // UserSearchBox
  const [searchQuery, setSearchQuery] = React.useState('');
  const [itemsSelected, setItemsSelected] = React.useState({
    users: []
  });

  const { isEnabled: useSelectOrgPackage } = useFeatureToggle(
    // @ts-ignore -- TODO add to feat const /Jonathan 2026-01-12
    'Configcat_selectPackageOnOrgCreation'
  );

  const marketInfo = useQuery(GET_ALL_MARKETS, { fetchPolicy: 'no-cache' });
  const [getUserInfo, userInfo] = useLazyQuery(GET_ALL_USERS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    onCompleted() {
      if (!userData) {
        setUserData(userInfo.data.get.data);
      } else {
        setUserData([...userData, ...userInfo.data.get.data]);
      }
    }
  });

  const allPackages = useQuery(GET_ALL_PACKAGES, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onCompleted() {
      const packagesData = allPackages.data.get
        .sort(sortPackages)
        .map((pkg) => ({
          Key: pkg.id,
          Value: t(`packages.${pkg.id}`),
          package: pkg
        }));
      setPackages([...packages, ...packagesData]);
    },
    skip: !useSelectOrgPackage
  });

  const [postOrganization, postOrganizationData] =
    useMutation(POST_ORGANIZATION);

  const fetchMoreUsers = () => {
    const url = userInfo.data.get.nextPage;
    const params = url.substring(url.indexOf('?') + 1);
    getUserInfo({
      variables: {
        query: `query=${encodeUserSearchQuery(searchQuery)}&${params}`
      }
    });
  };

  const removeItem = (key, type) => {
    setItemsSelected(() => {
      let newItems = itemsSelected.users.filter(
        (item) => item.fullName !== key
      );
      return {
        ...itemsSelected,
        [type]: newItems
      };
    });
  };

  useEffect(() => {
    const delay = setTimeout(() => {
      setUserData(undefined);
      getUserInfo({
        variables: { query: `query=${encodeUserSearchQuery(searchQuery)}` }
      });
    }, 300);
    return () => {
      clearTimeout(delay);
    };
  }, [searchQuery]);

  useEffect(() => {
    if (
      itemsSelected.users.length > 0 &&
      itemsSelected.users[0].id !==
        itemsSelected.users[itemsSelected.users.length - 1].id
    ) {
      setItemsSelected({
        users: [itemsSelected.users[itemsSelected.users.length - 1]]
      });
    }

    setSelectedValues((prevState) => ({
      ...prevState,
      user: itemsSelected.users[0]
    }));
  }, [itemsSelected]);

  const handleOrgNameChange = (e) => {
    const value = e.target.value;
    setOrgInputValue(value);
    setSelectedValues((prevState) => ({
      ...prevState,
      orgName: value
    }));
  };

  const handlePackageSelection = (index) => {
    setPackageValue(index);
    setSelectedValues((prevState) => ({
      ...prevState,
      package: packages[index]?.package
    }));
  };

  const createOrganization = () => {
    const orgName = selectedValues.orgName;
    const marketId = marketInfo.data.get[selectedValues.market].id;
    const user: User = selectedValues.user;
    const payload: Organization = {
      name: orgName,
      marketId,
      productFeatures: [],
      packages: useSelectOrgPackage ? [selectedValues.package] : [],
      organizationAdministrator: user,
      platform: DEFAULT_PLATFORM
    };

    postOrganization({
      variables: {
        input: payload
      },
      onCompleted(data) {
        localStorage.setItem('tenantId', data.post.organization.id);
        setIsCreateOrg(false);
        handleSetOrganization(data.post.organization.id);
      }
    });
  };

  const renderStatus = () => {
    let message = '';
    if (postOrganizationData.loading) {
      message = 'Creating organization...';
    } else {
      message = 'Successfully created organization!';
    }
    if (postOrganizationData.error) {
      message = `${postOrganizationData.error.message} - ${postOrganizationData.error.networkError.result.title}`;
    }
    return message;
  };

  return (
    <>
      <Container>
        {postOrganizationData.called ? (
          <ResponseMessage hasErrors={postOrganizationData.error}>
            {renderStatus()}
          </ResponseMessage>
        ) : (
          <>
            <StyledSubHeading>About organisation</StyledSubHeading>
            <TextInput
              className="input-field"
              name="TextInput"
              label="Organization Name"
              value={orgInputValue}
              placeholder="Organization Name"
              changeValue={handleOrgNameChange}
              required={true}
            />
            <SearchableDropDown
              className="input-field"
              label="Select market for organization"
              data={marketInfo.data?.get}
              loading={false}
              setSelectedIndex={(index) =>
                setSelectedValues((prevState) => ({
                  ...prevState,
                  market: index
                }))
              }
            />
            {useSelectOrgPackage && !allPackages.loading && (
              <>
                <Spacing padding="0.5rem" />
                <SingleSelect
                  label="Select package for organization"
                  dropdownText="package"
                  dropdownData={packages || []}
                  onClickSelectItem={handlePackageSelection}
                  withSortButton={false}
                  activeIndex={packageValue}
                  required={true}
                />
              </>
            )}
            <Spacing />
            <StyledSubHeading>Organisation Admin</StyledSubHeading>
            <p>
              Select user(s) added as admins for organisation binder. Select at
              least one.
            </p>
            <SearchBox
              className="input-field"
              placeholder={'Type to search an user or group'}
              suggestionsPosition={'bottom'}
              value={searchQuery}
              onValueChange={(value) => setSearchQuery(value)}
              showSuggestions={true}
              label={'Search for and add user'}
              onSearch={null}
              autoComplete={'off'}
              valueItalic={false}
              withButton={false}
              icon={search}
            >
              <UserSearchBox
                {...{
                  labels: {
                    addUser: 'Add user',
                    userNameFiltered: `Users with: `,
                    groupsWith: `Groups with: `,
                    addGroup: `Add all participants`,
                    inviteUsers: 'Invite a new user'
                  },
                  loading: false,
                  dataSet: userData,
                  setItemsSelected,
                  searchQuery,
                  itemsSelected,
                  isFetchingMore:
                    userInfo.networkStatus !== NetworkStatus.ready, //refetch
                  fetchMore: fetchMoreUsers,
                  fetchMoreBtnText: 'Fetch more users',
                  hasFetchMore: userInfo?.data?.get.nextPage !== null,
                  hasInviteUsers: false
                }}
              />
            </SearchBox>
            <div>
              <StyledSubHeading small>
                Added organization admins
              </StyledSubHeading>
              {itemsSelected.users.map((item, i) => (
                <UserChip
                  style={{ display: 'inline-flex' }}
                  key={i}
                  onClose={() => removeItem(item.fullName, 'users')}
                  fullName={item.fullName}
                />
              ))}
            </div>
          </>
        )}
      </Container>
      <BottomActionBar justifyContent="space-between">
        <Button
          onClick={onClose}
          label="Close"
          btnStyle="secondary"
          btnExtras={['labelWidth']}
        />
        {!postOrganizationData.called && (
          <Button
            onClick={createOrganization}
            label="Create"
            btnStyle="primary"
            btnExtras={['labelWidth']}
            disabled={
              selectedValues.market === null ||
              selectedValues.orgName === null ||
              !selectedValues.user ||
              (useSelectOrgPackage && !selectedValues.package)
            }
          />
        )}
      </BottomActionBar>
    </>
  );
};

export default CreateOrgForm;
